'use client';

import { useEffect, useRef } from 'react';

import { CompanyPersonSelectFields } from '@/features/contacts/components/CompanyPersonSelectFields';
import { JobServiceTypesSection } from '@/features/service-types/components';
import { JobTeamSection } from '@/features/workspaces/components';
import {
  Badge,
  DatePicker,
  MainButton,
  SectionTabs,
  SmartInput,
} from '@web/ui';

import styles from './JobSummaryStep.module.scss';

import type { JobClientMode, JobFormMode, JobSummaryDraft } from './types';
import type { Dayjs } from 'dayjs';

type JobSummaryStepProps = {
  mode: JobFormMode;
  totalSteps: number;
  summary: JobSummaryDraft;
  fieldErrors?: Record<string, string>;
  isSaving: boolean;
  readOnly?: boolean;
  metadataReadOnly?: boolean;
  jobNumberReadOnly?: boolean;
  onPatchDraft: (
    patch: Partial<JobSummaryDraft>,
    options?: { persist?: boolean; forcePersist?: boolean },
  ) => Promise<boolean>;
  onPatchMetadata?: (patch: Partial<JobSummaryDraft>) => Promise<boolean>;
  onContinue: () => void;
};

export function JobSummaryStep({
  mode,
  totalSteps,
  summary,
  fieldErrors = {},
  isSaving,
  readOnly: readOnlyProp,
  metadataReadOnly: metadataReadOnlyProp,
  jobNumberReadOnly: jobNumberReadOnlyProp,
  onPatchDraft,
  onPatchMetadata,
  onContinue,
}: JobSummaryStepProps) {
  const readOnly = readOnlyProp ?? mode !== 'new';
  const metadataReadOnly = metadataReadOnlyProp ?? readOnly;
  const jobNumberReadOnly = jobNumberReadOnlyProp ?? metadataReadOnly;
  const pendingNameRef = useRef(summary.name);
  const pendingJobNumberRef = useRef(summary.jobNumber);

  useEffect(() => {
    pendingNameRef.current = summary.name;
  }, [summary.name]);

  useEffect(() => {
    pendingJobNumberRef.current = summary.jobNumber;
  }, [summary.jobNumber]);
  const isProject = summary.jobKind === 'project';
  const isRetainer = summary.jobKind === 'retainer';
  const hasStartupFields = Boolean(
    summary.name.trim() && summary.jobLeadId.trim(),
  );
  const startupFieldsLocked = mode === 'new' && !hasStartupFields;
  const gatedReadOnly = readOnly || startupFieldsLocked;
  const patchJobLead = (jobLeadId: string) => {
    const teamUserIds =
      jobLeadId && !summary.teamUserIds.includes(jobLeadId)
        ? [...summary.teamUserIds, jobLeadId]
        : summary.teamUserIds;
    const shouldPersist =
      mode !== 'new' || Boolean(summary.name.trim() && jobLeadId.trim());

    void onPatchDraft({ jobLeadId, teamUserIds }, { persist: shouldPersist });
  };

  return (
    <section className={styles.jobSummaryStep}>
      <div>
        <Badge label={`Step 1 of ${totalSteps}`} tone="default" size="lg" />
      </div>

      <div className={styles.jobSummaryStep__header}>
        <h2 className={styles.jobSummaryStep__title}>Set up the Job</h2>
        <p className={styles.jobSummaryStep__subtitle}>
          Define what this job is and who is involved.
        </p>
        {fieldErrors['job-form'] ? (
          <p className={styles.jobSummaryStep__error}>
            {fieldErrors['job-form']}
          </p>
        ) : null}
      </div>

      <div className={styles.jobSummaryStep__panel}>
        <h3 className={styles.jobSummaryStep__sectionTitle}>Job identity</h3>
        <p className={styles.jobSummaryStep__sectionDescription}>
          Set the core identity for this job. Changes save as soon as you make
          them.
        </p>
        <div className={styles.jobSummaryStep__identityGrid}>
          <SmartInput
            type="text"
            name="job-name"
            label="Job Name (required)"
            value={summary.name}
            error={fieldErrors['job-name']}
            readOnly={metadataReadOnly}
            onChange={(value) => {
              if (metadataReadOnly) return;
              pendingNameRef.current = value;
              void onPatchDraft({ name: value });
            }}
            onBlur={() => {
              if (metadataReadOnly) return;
              if (onPatchMetadata) {
                void onPatchMetadata({ name: pendingNameRef.current });
                return;
              }
              void onPatchDraft({}, { persist: true, forcePersist: true });
            }}
          />

          <JobTeamSection
            readOnly={readOnly}
            showHeader={false}
            showTeamMembers={false}
            jobLeadId={summary.jobLeadId}
            teamUserIds={summary.teamUserIds}
            fieldError={fieldErrors['job-lead-id']}
            onJobLeadChange={patchJobLead}
            onTeamUserIdsChange={(teamUserIds) =>
              void onPatchDraft({ teamUserIds }, { persist: true })
            }
          />

          <SmartInput
            type="number"
            name="job-number"
            label="Job Number"
            value={summary.jobNumber}
            error={fieldErrors['job-number']}
            readOnly={jobNumberReadOnly || startupFieldsLocked}
            onChange={(value) => {
              if (jobNumberReadOnly || startupFieldsLocked) return;
              pendingJobNumberRef.current = value;
              void onPatchDraft({ jobNumber: value });
            }}
            onBlur={() => {
              if (jobNumberReadOnly || startupFieldsLocked) return;
              if (onPatchMetadata) {
                void onPatchMetadata({
                  jobNumber: pendingJobNumberRef.current,
                });
                return;
              }
              void onPatchDraft({}, { persist: true, forcePersist: true });
            }}
          />

          <div className={styles.jobSummaryStep__kindTabs}>
            <span className={styles.jobSummaryStep__tabsLabel}>Job Type</span>
            <SectionTabs
              sections={[
                { id: 'project', label: 'Project' },
                { id: 'retainer', label: 'Retainer' },
              ]}
              activeId={summary.jobKind}
              aria-label="Job type"
              onSectionChange={(id) => {
                if (gatedReadOnly) return;
                void onPatchDraft(
                  {
                    jobKind: id as JobSummaryDraft['jobKind'],
                    endDate: id === 'retainer' ? null : summary.endDate,
                  },
                  { persist: true },
                );
              }}
            />
          </div>
        </div>
      </div>

      <div className={styles.jobSummaryStep__panel}>
        <h3 className={styles.jobSummaryStep__sectionTitle}>
          Client / Internal
        </h3>
        <p className={styles.jobSummaryStep__sectionDescription}>
          Control whether this work is for a client or for internal operations.
        </p>
        <div className={styles.jobSummaryStep__clientTabs}>
          <SectionTabs
            sections={[
              { id: 'client', label: 'Client' },
              { id: 'internal', label: 'Internal' },
            ]}
            activeId={summary.clientMode}
            aria-label="Client or internal"
            onSectionChange={(id) => {
              if (gatedReadOnly) return;
              const nextMode = id as JobClientMode;
              void onPatchDraft(
                nextMode === 'internal'
                  ? { clientMode: nextMode, company: null, person: null }
                  : { clientMode: nextMode },
                { persist: true },
              );
            }}
          />
        </div>

        {summary.clientMode === 'client' ? (
          <CompanyPersonSelectFields
            layout="inline"
            disabled={gatedReadOnly}
            company={summary.company}
            person={summary.person}
            companyError={fieldErrors['company-contact-id']}
            personError={fieldErrors['person-contact-id']}
            onCompanyChange={(company, nextPerson) => {
              if (gatedReadOnly) return;
              void onPatchDraft(
                { company, person: nextPerson },
                { persist: true },
              );
            }}
            onPersonChange={(person, nextCompany) => {
              if (gatedReadOnly) return;
              void onPatchDraft(
                { person, company: nextCompany },
                { persist: true },
              );
            }}
          />
        ) : null}
      </div>

      <div className={styles.jobSummaryStep__panel}>
        <JobTeamSection
          readOnly={readOnly}
          teamMembersReadOnly={startupFieldsLocked}
          showJobLead={false}
          jobLeadId={summary.jobLeadId}
          teamUserIds={summary.teamUserIds}
          fieldError={fieldErrors['job-lead-id']}
          onJobLeadChange={patchJobLead}
          onTeamUserIdsChange={(teamUserIds) =>
            void onPatchDraft({ teamUserIds }, { persist: true })
          }
        />
      </div>

      <div className={styles.jobSummaryStep__panel}>
        <JobServiceTypesSection
          readOnly={gatedReadOnly}
          selectedServiceTypeIds={summary.serviceTypeIds}
          fieldError={fieldErrors['service_type_ids']}
          onSelectedServiceTypeIdsChange={(serviceTypeIds) =>
            void onPatchDraft({ serviceTypeIds }, { persist: true })
          }
        />
      </div>

      {isRetainer ? (
        <div className={styles.jobSummaryStep__panel}>
          <h3 className={styles.jobSummaryStep__sectionTitle}>
            Billing Structure
          </h3>
          <p className={styles.jobSummaryStep__sectionDescription}>
            Define how this retainer should be scoped commercially.
          </p>

          <div className={styles.jobSummaryStep__billingGrid}>
            <div className={styles.jobSummaryStep__kindTabs}>
              <span className={styles.jobSummaryStep__tabsLabel}>
                Structure
              </span>
              <SectionTabs
                sections={[
                  { id: 'month_to_month', label: 'Month to month' },
                  { id: 'fixed_term', label: 'Fixed term' },
                ]}
                activeId={summary.retainerBillingStructure}
                aria-label="Retainer billing structure"
                onSectionChange={(id) => {
                  if (gatedReadOnly) return;
                  const structure =
                    id as JobSummaryDraft['retainerBillingStructure'];
                  void onPatchDraft(
                    {
                      retainerBillingStructure: structure,
                      retainerContractMonths:
                        structure === 'fixed_term'
                          ? (summary.retainerContractMonths ?? 12)
                          : null,
                    },
                    { persist: false },
                  );
                }}
              />
            </div>

            {summary.retainerBillingStructure === 'fixed_term' ? (
              <SmartInput
                type="number"
                name="retainer-contract-months"
                label="Length (months)"
                value={summary.retainerContractMonths}
                readOnly={gatedReadOnly}
                onChange={(value) => {
                  if (gatedReadOnly) return;
                  void onPatchDraft(
                    {
                      retainerContractMonths: value === null ? null : value,
                    },
                    { persist: false },
                  );
                }}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={styles.jobSummaryStep__panel}>
        <h3 className={styles.jobSummaryStep__sectionTitle}>Dates</h3>
        <p className={styles.jobSummaryStep__sectionDescription}>
          Anchor the job with a start date and, for project work, a target end
          date.
        </p>
        <div className={styles.jobSummaryStep__datesGrid}>
          <DatePicker
            name="job-start-date"
            label="Start Date"
            value={summary.startDate}
            error={fieldErrors['job-start-date']}
            disabled={gatedReadOnly}
            readOnly={gatedReadOnly}
            onChange={(value: Dayjs | null) => {
              if (gatedReadOnly) return;
              void onPatchDraft({ startDate: value }, { persist: true });
            }}
          />

          {isProject ? (
            <DatePicker
              name="job-end-date"
              label="End Date"
              value={summary.endDate}
              error={fieldErrors['job-end-date']}
              disabled={gatedReadOnly}
              readOnly={gatedReadOnly}
              onChange={(value: Dayjs | null) => {
                if (gatedReadOnly) return;
                void onPatchDraft({ endDate: value }, { persist: true });
              }}
            />
          ) : null}
        </div>
      </div>

      <footer className={styles.jobSummaryStep__footer}>
        <MainButton
          ariaLabel="Continue"
          disabled={isSaving}
          isLoading={isSaving}
          onClick={onContinue}
        >
          Continue
        </MainButton>
      </footer>
    </section>
  );
}
